import Modal from '@betterfunnel/ui-core/Modal';
import Select from '@betterfunnel/ui-core/Select';
import { ContentItem, SectionData } from '@betterfunnel/types';
import { Dialog } from '@headlessui/react';
import { TagIcon } from '@heroicons/react/24/outline';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { useCallback, useContext, useEffect, useState } from 'react';
import QuillField from '../components/QuillField';
import { UserContext } from '../contexts/UserContext';
import { emptyConverter } from '../utils';

interface Props {
  section?: SectionData;
  open: boolean;
  onClose: () => void;
  onAdd: (item: ContentItem) => void;
}

export default function AddSectionItemModal({
  section,
  open,
  onClose,
  onAdd,
}: Props) {
  const { selectedAccount } = useContext(UserContext);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();
  const [comparisonCategories, setComparisonCategories] =
    useState<ContentItem[]>();
  const [selectedCategory, setSelectedCategory] = useState<ContentItem>();
  const [comparisonItems, setComparisonItems] = useState<ContentItem[]>();
  const [selectedItem, setSelectedItem] = useState<ContentItem>();

  useEffect(() => {
    if (!selectedAccount) return;
    if (!section) return;
    if (!open) return;

    const type = section.type;
    if (type !== 'comparisonList' && type !== 'comparisonTopItems') return;

    const defaultCategory = (section.items[0] as any)?.category;

    const unsubscribe = onSnapshot(
      query(
        collection(selectedAccount.ref, 'contents'),
        where('status', '==', 'active'),
        where('type', '==', 'comparisonCategory'),
      ).withConverter(emptyConverter<ContentItem>()),
      (items) => {
        const arr = items.docs.map((d) => d.data());
        setComparisonCategories(arr);
        setSelectedCategory(
          arr.find((a) => a.id === defaultCategory) ?? arr[0],
        );
        setSelectedItem(undefined);
      },
    );

    return () => {
      unsubscribe();
    };
  }, [selectedAccount, section, open]);

  useEffect(() => {
    if (!selectedAccount) return;
    if (!selectedCategory || !comparisonCategories) {
      setComparisonItems(undefined);
      setSelectedItem(undefined);
      return;
    }

    const unsubscribe = onSnapshot(
      query(
        collection(selectedAccount.ref, 'contents'),
        where('status', '==', 'active'),
        where('type', '==', 'comparisonItem'),
        where('category', '==', selectedCategory.id),
      ).withConverter(emptyConverter<ContentItem>()),
      (items) => {
        const arr = items.docs.map((d) => d.data());
        setComparisonItems(arr);
        setSelectedItem(arr[0]);
      },
    );

    return () => {
      unsubscribe();
    };
  }, [selectedAccount, comparisonCategories, selectedCategory, section]);

  const onAddClick = useCallback(() => {
    if (!open) return;

    if (!selectedCategory || !selectedItem) {
      setError('Please select item');
      return;
    }

    onAdd(selectedItem);
  }, [selectedCategory, selectedItem, onAdd, open]);

  const _onClose = useCallback(() => {
    setError(undefined);
    setLoading(false);
    setComparisonCategories(undefined);
    setComparisonItems(undefined);
    setSelectedCategory(undefined);
    setSelectedItem(undefined);
    onClose();
  }, [onClose]);

  return (
    <Modal
      open={open}
      onClose={_onClose}
      actions={[
        {
          title: 'Cancel',
          type: 'secondary',
          disabled: loading,
          onClick: _onClose,
        },
        {
          title: 'Create',
          type: 'primary',
          loading: loading,
          onClick: onAddClick,
        },
      ]}
    >
      <Dialog.Title
        as="h3"
        className="text-base font-semibold leading-6 text-gray-900"
      >
        Select item to add
      </Dialog.Title>
      <div className="flex flex-col gap-4 mt-4">
        <Select<ContentItem | null>
          title="Category"
          selected={selectedCategory || null}
          onChange={(e) => {
            setSelectedCategory(e || undefined);
          }}
          loading={!comparisonCategories}
          options={
            comparisonCategories?.map((category) => ({
              key: category.id,
              value: category,
              child: (
                <span className="flex items-center gap-2">
                  <TagIcon className="h-4 w-4" />
                  {category.name}
                </span>
              ),
            })) || []
          }
        />

        <Select<ContentItem | null>
          title="Item"
          selected={selectedItem || null}
          onChange={(e) => {
            setSelectedItem(e || undefined);
          }}
          loading={
            !comparisonCategories || (selectedCategory && !comparisonItems)
          }
          options={
            comparisonItems?.map((item) => ({
              key: item.id,
              value: item,
              child: (
                <span className="flex items-center gap-2">
                  <TagIcon className="h-4 w-4" />
                  <div className="-ms-4 -my-0.5">
                    <QuillField
                      key={item.id}
                      value={item.name || item.brandName}
                      className="p-0"
                      readOnly
                      noDesign
                    />
                  </div>
                </span>
              ),
            })) || []
          }
        />
      </div>
      {error ? (
        <p className="mt-4 w-full text-sm text-end text-red-600">{error}</p>
      ) : (
        <></>
      )}
    </Modal>
  );
}
